import { DSModal } from "@components/design-system/organisms";
import {
  faCheck,
  faInfoCircle,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { usePimsExportFormat } from "@hooks";
import { useStartCompanionChat } from "@hooks/use-start-companion-chat";
import { useWritingAssistant } from "@hooks/use-writing-assistant";
import { TextareaAutosize } from "@mui/base";
import { Tooltip } from "@mui/material";
import { writeToClipboardWithPimsFormat } from "@src/utils/clipboard";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useController, useFormContext } from "react-hook-form";

import Twemoji from "../../../../Twemoji";
import { alert } from "../../../common/Alert";
import PredictiveInput from "../../../common/inputs/PredictiveInput";
import CascadingDropdown from "../../../common/menus/CascadingDropdown";
import UpgradeModal from "../../../common/premium/UpgradeModal";
import CustomizationFeedback from "../../common/CustomizationFeedback";
import CopyIndividualField from "../../edit/form/CopyIndividualField";
import { languageCodeToLanguageName } from "../../edit/form/utils";

export function NoteInput({
  fieldName,
  control,
  title,
  headerStyle,
  boxStyle,
  id,
  placeholder = null,
  noteUuid,
  onMouseUp = null,
  aiAssistantEnabled = false,
  languageAiAssistantEnabled = false,
  subheadingsAiAssistantEnabled = false,
}) {
  const { field } = useController({
    name: fieldName,
    control,
  });
  const { getValues, setValue } = useFormContext();
  const { allowCompanionOptIn } = useFlags();
  const { startChat, isStarting } = useStartCompanionChat();

  const {
    afterContent,
    isLoading,
    isStreaming,
    loadingBeforeStreaming,
    loadingBeforeAndDuringStreaming,
    options: updatedOptions,
    isMenuOpen,
    setIsMenuOpen,
    acceptChanges,
    rejectChanges,
    translationModal,
    customizationFeedback,
    showUpgradeModal,
    setShowUpgradeModal,
    isShowingExtraContent,
  } = useWritingAssistant({
    entityType: "note",
    entityUuid: noteUuid,
    fieldName,
    getFieldValue: () => getValues(fieldName),
    setFieldValue: (value) => setValue(fieldName, value),
    getOtherFieldValue: (fn) => getValues(fn),
    setOtherFieldValue: (fn, value) => setValue(fn, value),
    languageAiAssistantEnabled,
    subheadingsAiAssistantEnabled,
  });

  const inputClassName = ` border ${
    isShowingExtraContent
      ? "border-0 bg-transparent"
      : `rounded-xl mb-4 ${boxStyle}`
  } p-4 mt-2 w-full text-xs md:text-sm h-24 align-top text-gray-700 dark:text-gray-300 resize-none`;
  const extraInputClassName = `border border-t-0 ${boxStyle} p-1 mb-8 rounded-b-md w-full text-xs md:text-sm align-top text-gray-700 dark:text-gray-300 resize-none`;

  const pimsFormat = usePimsExportFormat();

  const copyText = () => {
    const textToCopy = getValues();
    writeToClipboardWithPimsFormat(textToCopy[fieldName], pimsFormat)
      .then(() => {
        mixpanel.track("Copied Note", {
          copyType: "legacy_field",
          pimsExportFormat: pimsFormat,
        });
      })
      .catch(() => alert("error", "Failed to copy to clipboard."));
  };

  return (
    <>
      <UpgradeModal
        shouldShow={showUpgradeModal}
        setShouldShow={setShowUpgradeModal}
        featureName="Writing Assistant"
      />
      <DSModal
        isOpen={translationModal.isShowing}
        onClose={translationModal.onDismiss}
        title="Translation Detected"
        dataTestId="translation-detected-modal"
        actions={[
          {
            label: "Only Translate Subjective",
            variant: "cancel",
            onClick: translationModal.onDismiss,
          },
          {
            label: "Translate All",
            variant: "primary",
            onClick: translationModal.onTranslateAll,
          },
        ]}
      >
        <h5 className="text-sm">
          We noticed that you translated the Subjective section of the
          note to{" "}
          {languageCodeToLanguageName(translationModal.language)}.
          Would you like the rest of your note to be translated to{" "}
          {languageCodeToLanguageName(translationModal.language)}?
        </h5>
      </DSModal>
      <div
        id={id}
        className="relative flex-1"
        data-testid={`${title}-input`}
      >
        <div className="absolute z-10 -top-3 left-0 flex flex-row items-center justify-items-center gap-1">
          {title && (
            <div
              className={`rounded-tr-full rounded-tl-full rounded-br-full py-1 px-4 ${headerStyle}`}
              data-testid="input-title"
            >
              <div className="flex">
                <h4 className="text-white dark:text-gray-100 whitespace-nowrap">
                  {title}
                </h4>
              </div>
            </div>
          )}
          {!aiAssistantEnabled ? null : isShowingExtraContent ? (
            <div
              className={`border ${boxStyle} flex items-center rounded-xl ml-1 divide-x`}
            >
              <button
                className="hover:bg-green-600 bg-green-500 text-white text-xs font-medium px-2 py-1 rounded-l-xl transition-all"
                onClick={acceptChanges}
                disabled={isLoading}
                data-testid="accept-button"
              >
                Accept <FontAwesomeIcon icon={faCheck} />
              </button>
              <button
                className="hover:bg-red-600 bg-red-500 text-white text-xs font-medium px-2 py-1 rounded-r-xl transition-all"
                onClick={rejectChanges}
                disabled={isLoading}
                data-testid="reject-button"
              >
                Reject <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ) : (
            <div className="flex flex-row gap-1">
              <CascadingDropdown
                options={updatedOptions}
                setIsMenuOpen={setIsMenuOpen}
                title={title}
                isTriggerComponentDisabled={
                  loadingBeforeAndDuringStreaming
                }
                triggerComponent={({ onClick }) => (
                  <div
                    className={`h-8 w-8 flex items-center justify-items-center p-1.5 rounded-full ${
                      loadingBeforeAndDuringStreaming
                        ? "bg-gray-400 cursor-not-allowed"
                        : "cursor-pointer shadow-md"
                    } ${
                      !loadingBeforeAndDuringStreaming && isMenuOpen
                        ? "bg-indigo-600 border-yellow-200"
                        : !loadingBeforeAndDuringStreaming
                          ? "bg-indigo-500 border-indigo-500"
                          : "border-gray-400"
                    } border-2 ${
                      !loadingBeforeAndDuringStreaming &&
                      "hover:bg-indigo-600 hover:border-yellow-200 transition-all"
                    }`}
                    onClick={onClick}
                    title="Trigger Writing Assistant"
                    data-testid="cascading-dropdown-trigger"
                  >
                    <Twemoji className="text-xl z-20" emoji="✨" />
                  </div>
                )}
              />
              {allowCompanionOptIn && (
                <div
                  className={`h-8 w-8 flex items-center justify-center p-1.5 rounded-full border-2 ${
                    isStarting
                      ? "bg-gray-400 border-gray-400 cursor-not-allowed"
                      : "bg-indigo-500 border-indigo-500 cursor-pointer shadow-md hover:bg-indigo-600 hover:border-yellow-200 transition-all"
                  }`}
                  onClick={() => {
                    if (isStarting) return;
                    startChat();
                  }}
                  title="Edit with Quill"
                  data-testid="edit-with-quill-trigger"
                >
                  <Twemoji className="text-xl z-20" emoji="🦔" />
                </div>
              )}
            </div>
          )}
        </div>

        {loadingBeforeStreaming ||
          (!getValues(fieldName) && isStreaming && (
            <div className="absolute inset-0 flex justify-center items-center z-30">
              <FontAwesomeIcon
                data-testid="loading-spinner"
                icon={faSpinner}
                className="animate-spin text-gray-500 dark:text-gray-400 text-xl"
              />
            </div>
          ))}
        {isShowingExtraContent ? (
          <div
            className={`flex flex-row space-x-4 h-full ${boxStyle} border rounded-t-xl`}
          >
            <TextareaAutosize
              className={inputClassName}
              value={afterContent}
              data-testid="after-content-textarea"
              placeholder="Loading..."
              readOnly
            />
            <div className="w-full border-l px-4 pb-4">
              <h4 className="my-2">
                Before{" "}
                <Tooltip
                  arrow
                  placement="right"
                  title="Click 'Reject' to keep this version."
                >
                  <span>
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </span>
                </Tooltip>
              </h4>
              <TextareaAutosize
                className={
                  "italic w-full text-xs md:text-sm bg-transparent align-top text-gray-700 dark:text-gray-300 resize-none"
                }
                value={getValues(fieldName)}
                onChange={(e) => setValue(fieldName, e.target.value)}
                readOnly
                data-testid="before-content-textarea"
                placeholder="Before content"
              />
            </div>
          </div>
        ) : (
          <>
            <CopyIndividualField
              onClick={copyText}
              dataTestId="copy-button"
            />
            <PredictiveInput
              isStreaming={isStreaming}
              field={field}
              placeholder={
                !placeholder
                  ? `Type ${title} notes here...`
                  : placeholder
              }
              inputClassName={inputClassName}
              onMouseUp={onMouseUp}
              disabled={isShowingExtraContent}
            />
          </>
        )}

        {isShowingExtraContent &&
          customizationFeedback.hasFeedbackAccess && (
            <div
              className={extraInputClassName}
              data-testid="extra-content"
            >
              <CustomizationFeedback
                llmCustomizationUuid={
                  customizationFeedback.llmCustomizationUuid
                }
              />
            </div>
          )}
      </div>
    </>
  );
}

export default NoteInput;
